import { inject } from '@angular/core';
import { redirectUnauthorizedTo } from '@angular/fire/auth-guard';
import { Router } from '@angular/router';
import { AuthService } from '@services/http/auth.service';
import { UserService } from 'app/services/user.service';
import { catchError, map, of, switchMap, take } from 'rxjs';

export const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['profile/login']);

export const isNotLogedInGuard = (_auth: AuthService = inject(AuthService), _router: Router = inject(Router)) => {
  return _auth.authStatusSub.pipe(
    take(1),
    map(user => {
      if (user) _router.navigate(['dashboard']);
      return !user;
    })
  );
};

export const notRegisteredGuard = (
  _auth: AuthService = inject(AuthService),
  _userService: UserService = inject(UserService),
  _router: Router = inject(Router)
) => {
  return _auth.authStatusSub.pipe(
    take(1),
    map(user => {
      if (user) {
        const cachedUser = _userService.getCachedUser(user.uid);
        if (!cachedUser || (cachedUser && !cachedUser.dateRegistered)) {
          return true;
        } else _router.navigate(['dashboard']);
      } else _router.navigate(['profile/login']);
      return false;
    })
  );
};

export const userLoadedGuard = (
  _userService: UserService = inject(UserService),
  _router: Router = inject(Router),
  _auth: AuthService = inject(AuthService)
) => {
  return _auth.authStatusSub.pipe(
    take(1),
    switchMap(user => {
      if (user) {
        // User is authenticated
        const cachedUser = _userService.getCachedUser(user.uid);

        if (cachedUser) {
          if (!cachedUser.dateRegistered) {
            _router.navigate(['./profile/account-details']);
            return of(false);
          }
          return of(true);
        }

        return _userService.getUser(user.uid).pipe(
          map(value => {
            if (!value) return false;
            if (value && !value?.dateRegistered) {
              _router.navigate(['./profile/account-details']);
              return false;
            }
            return true;
          }),
          catchError(() => {
            _userService.register({ email: user.email, externalUserID: user.uid });
            return of(false);
          })
        );
      } else {
        // User is not authenticated, navigate to login page
        _router.navigate(['./profile/login']);
        return of(false);
      }
    })
  );
};
